import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiUser, FiLock, FiEye, FiEyeOff, FiFileText, FiBarChart3, FiSettings, FiLogOut, FiMenu, FiX, FiUploadCloud, FiCheckCircle, FiAlertCircle } = FiIcons;

const Portal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  
  // Ticket / Upload States
  const [ticketTitle, setTicketTitle] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [ticketSuccess, setTicketSuccess] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    setIsLoggedIn(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setUploadError('');
    setSelectedFile(null);

    if (file) {
      const validExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
      const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

      // Expliziter Block für .txt und .md sowie Dateigrößen-Check (Max 5 MB)
      if (
        file.size > 5 * 1024 * 1024 || 
        !validExtensions.includes(fileExtension) || 
        fileExtension === '.txt' || 
        fileExtension === '.md'
      ) {
        setUploadError('Maximal 5 MB erlaubt. Nur PDF oder Bilder.');
        e.target.value = ''; // Input zurücksetzen
        return;
      }

      setSelectedFile(file);
    }
  };

  const handleTicketSubmit = (e) => {
    e.preventDefault();
    if (uploadError) return;
    
    setTicketSuccess(true);
    setTicketTitle('');
    setTicketDescription('');
    setSelectedFile(null);
    e.target.reset();

    setTimeout(() => setTicketSuccess(false), 5000);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white p-8 rounded-2xl shadow-xl w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Kundenportal</h1>
            <p className="text-gray-600">Hubler & Tex Facility Services</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">E-Mail</label>
              <input type="email" required className="w-full px-4 py-3 border rounded-lg" placeholder="demo@hublertex.ch" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Passwort</label>
              <input type="password" required className="w-full px-4 py-3 border rounded-lg" placeholder="••••••••" />
            </div>
            
            <div className="space-y-4">
              <button className="w-full bg-orange-500 text-white py-3 rounded-lg font-bold hover:bg-orange-600 transition-colors">
                Anmelden
              </button>
              <div className="flex justify-center pb-2">
                <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 shadow-sm">
                  🚧 Datenbank-Anbindung im Aufbau
                </span>
              </div>
            </div>
          </form>
          <div className="mt-6 p-4 bg-orange-50 rounded-lg text-xs text-orange-700">
            <strong>Demo-Zugang:</strong> demo@hublertex.ch / demo123
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        <header className="flex justify-between items-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Willkommen bei Hubler & Tex</h1>
          <button onClick={() => setIsLoggedIn(false)} className="flex items-center space-x-2 text-gray-600 hover:text-orange-500 font-medium">
            <SafeIcon icon={FiLogOut} />
            <span className="hidden sm:inline">Abmelden</span>
          </button>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12">
          <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <SafeIcon icon={FiFileText} className="text-orange-500 w-8 h-8 mb-4" />
            <h3 className="font-bold text-lg mb-2">Rechnungen</h3>
            <p className="text-gray-600 text-sm">Übersicht über alle offenen und bezahlten Posten.</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <SafeIcon icon={FiBarChart3} className="text-orange-500 w-8 h-8 mb-4" />
            <h3 className="font-bold text-lg mb-2">Statistik</h3>
            <p className="text-gray-600 text-sm">Ihre Service-Nutzung im Überblick.</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-shadow">
            <SafeIcon icon={FiSettings} className="text-orange-500 w-8 h-8 mb-4" />
            <h3 className="font-bold text-lg mb-2">Profil</h3>
            <p className="text-gray-600 text-sm">Verwalten Sie Ihre Objektdaten.</p>
          </div>
        </div>

        {/* Neues Ticket / Upload Sektion */}
        <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8 max-w-3xl">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Neues Service-Ticket erstellen</h2>
          
          {ticketSuccess && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
              <SafeIcon icon={FiCheckCircle} className="w-5 h-5 mr-2" />
              <span>Ticket erfolgreich erstellt! Wir kümmern uns umgehend darum.</span>
            </motion.div>
          )}

          <form onSubmit={handleTicketSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Betreff</label>
              <input 
                type="text" 
                required 
                value={ticketTitle}
                onChange={(e) => setTicketTitle(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" 
                placeholder="Kurze Beschreibung des Anliegens" 
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Details</label>
              <textarea 
                required 
                rows={4}
                value={ticketDescription}
                onChange={(e) => setTicketDescription(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500" 
                placeholder="Bitte beschreiben Sie Ihr Anliegen detailliert..." 
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dokument oder Foto anhängen (optional)</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-orange-500 transition-colors bg-gray-50">
                <div className="space-y-1 text-center">
                  <SafeIcon icon={FiUploadCloud} className="mx-auto h-10 w-10 text-gray-400" />
                  <div className="flex text-sm text-gray-600 justify-center">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-orange-600 hover:text-orange-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-orange-500 px-2 py-1">
                      <span>Datei auswählen</span>
                      <input 
                        id="file-upload" 
                        name="file-upload" 
                        type="file" 
                        className="sr-only" 
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChange}
                      />
                    </label>
                    <p className="pl-1 pt-1">oder Drag & Drop</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Erlaubte Formate: PDF, JPG, PNG (Max. 5 MB)
                  </p>
                </div>
              </div>
              
              {selectedFile && !uploadError && (
                <p className="mt-2 text-sm text-green-600 flex items-center">
                  <SafeIcon icon={FiCheckCircle} className="w-4 h-4 mr-1" />
                  {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}

              {uploadError && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-2 text-sm text-red-500 flex items-center font-medium">
                  <SafeIcon icon={FiAlertCircle} className="w-4 h-4 mr-1" />
                  {uploadError}
                </motion.p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center pt-4 gap-4 border-t border-gray-100 mt-6">
              <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200 shadow-sm">
                🚧 Datenbank-Anbindung im Aufbau
              </span>
              <button 
                type="submit" 
                disabled={!!uploadError}
                className="w-full sm:w-auto bg-orange-500 text-white px-8 py-3 rounded-lg font-bold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Ticket absenden
              </button>
            </div>
          </form>
        </div>

      </div>
    </div>
  );
};

export default Portal;